import { useMemo, useState } from 'react';
import { createChargerOnBackend, createSimulatorOnDaemon, type PortSpec } from '../lib/api';

interface Props {
  onClose: () => void;
  onDone: () => Promise<void> | void;
}

interface ParsedRow {
  charger_id: string;
  name: string;
  lat: number;
  lng: number;
  location: string;
  port_count: number;
  connector_type: string;
  power_kw: number;
  is_public: boolean;
}

interface Progress {
  charger_id: string;
  status: 'pending' | 'ok' | 'skipped' | 'failed';
  error?: string;
}

const HEADERS_REQUIRED = ['charger_id', 'lat', 'lng'];
const HEADERS_OPTIONAL = ['name', 'location', 'port_count', 'connector_type', 'power_kw', 'is_public'];

const EXAMPLE_CSV = `charger_id,name,lat,lng,location,port_count,connector_type,power_kw,is_public
SIMV11020,Central Mall,17.4400,78.4900,MG Road,2,20A Socket - Max 3.3kW,3.3,true
SIMV11021,Airport Lot A,17.2400,78.4290,,4,Type 2 Gun - Max 7.2kW,7.2,true
SIMV11022,Private Depot,17.4800,78.4100,,1,20A Socket - Max 3.3kW,3.3,false`;

/**
 * Minimal CSV parser tuned for the shape we accept — comma-separated,
 * optional double-quoted fields with "" escaping. Skips blank lines and
 * lines starting with #. First non-comment line MUST be a header row so
 * users can reorder columns freely.
 */
function parseCsv(text: string): Array<Record<string, string>> {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '' && !l.trim().startsWith('#'));
  if (lines.length === 0) return [];
  const header = splitLine(lines[0]!).map((h) => h.trim().toLowerCase());
  const rows: Array<Record<string, string>> = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitLine(lines[i]!);
    const row: Record<string, string> = {};
    for (let j = 0; j < header.length; j++) {
      row[header[j]!] = (cells[j] ?? '').trim();
    }
    rows.push(row);
  }
  return rows;
}

function splitLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQuotes = false;
      else cur += ch;
    } else {
      if (ch === ',') { out.push(cur); cur = ''; }
      else if (ch === '"' && cur === '') inQuotes = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function validateAndCoerce(rows: Array<Record<string, string>>): { rows: ParsedRow[]; errors: string[] } {
  const errors: string[] = [];
  const out: ParsedRow[] = [];

  if (rows.length === 0) { errors.push('No data rows found.'); return { rows: [], errors }; }

  // Header check.
  const missing = HEADERS_REQUIRED.filter((h) => !(h in rows[0]!));
  if (missing.length > 0) errors.push(`Missing required column(s): ${missing.join(', ')}`);

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]!;
    const rowNum = i + 2;               // account for header line
    const charger_id = (r['charger_id'] ?? '').trim().toUpperCase();
    const lat = Number(r['lat']);
    const lng = Number(r['lng']);
    if (!charger_id) { errors.push(`Row ${rowNum}: charger_id empty`); continue; }
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) { errors.push(`Row ${rowNum}: bad lat "${r['lat']}"`); continue; }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) { errors.push(`Row ${rowNum}: bad lng "${r['lng']}"`); continue; }

    const port_count = clampInt(Number(r['port_count'] || '1'), 1, 4);
    const power_kw = Number(r['power_kw'] || '3.3');
    if (!Number.isFinite(power_kw) || power_kw <= 0) { errors.push(`Row ${rowNum}: bad power_kw`); continue; }
    const isPublicRaw = (r['is_public'] ?? 'true').trim().toLowerCase();
    const is_public = isPublicRaw !== 'false' && isPublicRaw !== '0' && isPublicRaw !== 'no';

    out.push({
      charger_id,
      name: r['name']?.trim() || charger_id,
      lat, lng,
      location: r['location']?.trim() ?? '',
      port_count,
      connector_type: r['connector_type']?.trim() || '20A Socket - Max 3.3kW',
      power_kw,
      is_public,
    });
  }
  return { rows: out, errors };
}

function clampInt(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.max(lo, Math.min(hi, Math.round(n)));
}

export default function CsvBulkAddModal({ onClose, onDone }: Props) {
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<Progress[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { parsed, validationErrors } = useMemo(() => {
    if (!csv.trim()) return { parsed: [] as ParsedRow[], validationErrors: [] as string[] };
    const raw = parseCsv(csv);
    const { rows, errors } = validateAndCoerce(raw);
    return { parsed: rows, validationErrors: errors };
  }, [csv]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (validationErrors.length > 0) { setError('Fix validation errors before importing.'); return; }
    if (parsed.length === 0) { setError('Nothing to import.'); return; }

    setBusy(true);
    const prog: Progress[] = parsed.map((r) => ({ charger_id: r.charger_id, status: 'pending' }));
    setProgress(prog);

    for (let i = 0; i < parsed.length; i++) {
      const r = parsed[i]!;
      try {
        const ports: PortSpec[] = Array.from({ length: r.port_count }, (_, j) => ({
          port_number: j + 1,
          connector_type: r.connector_type,
        }));
        try {
          await createChargerOnBackend({
            charger_id: r.charger_id,
            name: r.name,
            lat: r.lat,
            lng: r.lng,
            location: r.location,
            power_kw: r.power_kw,
            is_public: r.is_public,
            ports,
          });
        } catch (err: any) {
          // 409 means the charger already exists — we still want to
          // attach it on the daemon side, so swallow this specific case.
          if (err?.response?.status !== 409) throw err;
        }
        await createSimulatorOnDaemon(r.charger_id, r.port_count);
        prog[i] = { ...prog[i]!, status: 'ok' };
      } catch (err: any) {
        const msg = err?.response?.data?.error ?? err?.message ?? 'failed';
        prog[i] = { ...prog[i]!, status: 'failed', error: msg };
      }
      setProgress([...prog]);
    }

    await onDone();
    setBusy(false);
  }

  const okCount = progress.filter((p) => p.status === 'ok').length;
  const failedCount = progress.filter((p) => p.status === 'failed').length;
  const skippedCount = progress.filter((p) => p.status === 'skipped').length;
  const done = progress.length > 0 && progress.every((p) => p.status !== 'pending');

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <form className="modal" style={{ maxWidth: 720 }} onSubmit={submit}>
        <h2>CSV import</h2>

        {progress.length === 0 ? (
          <>
            <div className="muted" style={{ marginBottom: 8 }}>
              Paste comma-separated rows below. Required columns: <code>{HEADERS_REQUIRED.join(', ')}</code>.
              Optional: <code>{HEADERS_OPTIONAL.join(', ')}</code>. Blank lines and lines starting with <code>#</code> are ignored.
            </div>
            <textarea
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              placeholder={EXAMPLE_CSV}
              rows={12}
              style={{
                width: '100%',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 12,
                background: 'var(--bg)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 10,
                resize: 'vertical',
                whiteSpace: 'pre',
              }}
            />
            <div className="row between mt">
              <div className="muted">
                {parsed.length > 0 && `${parsed.length} row${parsed.length === 1 ? '' : 's'} parsed`}
                {validationErrors.length > 0 && ` · ${validationErrors.length} issue${validationErrors.length === 1 ? '' : 's'}`}
              </div>
              <button
                type="button"
                className="ghost"
                onClick={() => setCsv(EXAMPLE_CSV)}
              >Load example</button>
            </div>
            {validationErrors.length > 0 && (
              <div style={{ marginTop: 8, maxHeight: 140, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
                {validationErrors.slice(0, 20).map((e, i) => (
                  <div key={i} style={{ fontSize: 12, color: 'var(--danger)' }}>{e}</div>
                ))}
                {validationErrors.length > 20 && <div className="muted" style={{ fontSize: 12 }}>…and {validationErrors.length - 20} more</div>}
              </div>
            )}
            <div className="row mt" style={{ gap: 8 }}>
              <button type="submit" disabled={busy || parsed.length === 0 || validationErrors.length > 0} style={{ flex: 1 }}>
                Import {parsed.length}
              </button>
              <button type="button" className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div className="muted" style={{ marginBottom: 12 }}>
              {okCount} imported · {skippedCount} skipped · {failedCount} failed · {progress.length - okCount - skippedCount - failedCount} pending
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
              {progress.map((p) => (
                <div key={p.charger_id} className="row between" style={{ padding: '4px 0', fontSize: 13 }}>
                  <span>{p.charger_id}</span>
                  <span className={`pill ${p.status === 'ok' ? 'online' : p.status === 'failed' ? 'offline' : p.status === 'skipped' ? 'outage' : ''}`} style={{ fontSize: 11 }}>
                    {p.status === 'pending' ? '…' : p.status}
                    {p.error && ` — ${p.error}`}
                  </span>
                </div>
              ))}
            </div>
            <div className="row mt" style={{ gap: 8 }}>
              <button type="button" onClick={onClose} disabled={!done} style={{ flex: 1 }}>
                {done ? 'Close' : 'Working…'}
              </button>
            </div>
          </>
        )}
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}
